import { isBoolean, isNumber } from 'lodash';

/**
 * Export: RQB -> JsonLogic
 * JsonLogic Rule Processor extended for custom operators
 * Usage in formatQuery in options.ruleProcessor
 * @doc https://react-querybuilder.js.org/docs/utils/export#rule-processor
 * @see libs/shared/domain/src/lib/utils/json-logic-extended.ts
 * @param rule
 * @returns
 */
export const ruleProcessorJsonLogicExtended = (rule: {
  operator: string;
  value: unknown;
  field: unknown;
}): object | null => {
  switch (rule.operator) {
    // @NOTE: For numeric values clearing the value will set it to null (considered as null operator in jsonLogic validation
    case '!=':
    case '=':
      if (!rule.value && !isNumber(rule.value) && !isBoolean(rule.value)) {
        return {
          [rule.operator === '=' ? '==' : rule.operator]: [
            { var: rule.field },
            '',
          ],
        };
      }
      return null;
    // @NOTE: Needed to avoid this rule to be automatically filtered out when formatted to jsonLogic
    case 'between':
    case 'notBetween':
      if (
        !rule.value ||
        rule.value === '' ||
        (typeof rule.value === 'string' && !rule.value.includes(','))
      ) {
        if (rule.operator === 'between') {
          return {
            '<=': [null, { var: rule.field }, null],
          };
        }

        return {
          '!': {
            '<=': [null, { var: rule.field }, null],
          },
        };
      }
      return null;
    // Custom operators are not handled by the default processor
    case 'doesNotMatchRegexp':
      return { doesNotMatchRegexp: [rule.value, { var: rule.field }] };
    case 'matchRegexp':
      return { matchRegexp: [rule.value, { var: rule.field }] };
    case 'arrayContains':
      return { arrayContains: [rule.value, { var: rule.field }] };
    case 'arrayContainsAll':
      return { arrayContainsAll: [rule.value, { var: rule.field }] };
    case 'arrayContainsExactly':
      return { arrayContainsExactly: [rule.value, { var: rule.field }] };
    case 'doesNotArrayContain':
      return { doesNotArrayContain: [rule.value, { var: rule.field }] };
    case 'doesNotArrayContainAll':
      return { doesNotArrayContainAll: [rule.value, { var: rule.field }] };
    default:
      // Defer to the default processor for all other operators
      return null;
  }
};

/**
 * Import: JsonLogic -> RQB
 * Json logic operation extended for custom operators
 * Usage in parseJsonLogic in options.jsonLogicOperations
 * @doc https://react-querybuilder.js.org/docs/utils/import#custom-operations
 * @see libs/shared/domain/src/lib/utils/json-logic-extended.ts
 */
export const jsonLogicOperationsExtended: Record<
  string,
  // any from the lib type, stay with any right now
  // could dig in RulesLogic from jsonLogic typing
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (value: any) => { field: string; operator: string; value: string } | false
> = {
  matchRegexp: (val) => ({
    field: val[1].var,
    operator: 'matchRegexp',
    value: val[0],
    valueSource: 'value',
  }),
  doesNotMatchRegexp: (val) => ({
    field: val[1].var,
    operator: 'doesNotMatchRegexp',
    value: val[0],
    valueSource: 'value',
  }),
  arrayContains: (val) => ({
    field: val[1].var,
    operator: 'arrayContains',
    value: val[0],
    valueSource: 'value',
  }),
  arrayContainsAll: (val) => ({
    field: val[1].var,
    operator: 'arrayContainsAll',
    value: val[0],
    valueSource: 'value',
  }),
  arrayContainsExactly: (val) => ({
    field: val[1].var,
    operator: 'arrayContainsExactly',
    value: val[0],
    valueSource: 'value',
  }),
  doesNotArrayContain: (val) => ({
    field: val[1].var,
    operator: 'doesNotArrayContain',
    value: val[0],
    valueSource: 'value',
  }),
  doesNotArrayContainAll: (val) => ({
    field: val[1].var,
    operator: 'doesNotArrayContainAll',
    value: val[0],
    valueSource: 'value',
  }),
};
